import { computed, onMounted, onUnmounted, ref, type ComputedRef } from 'vue';

import { MessageEvents, useUnreadMessageCount } from '@/entities/message';
import type { UnreadMessageCount } from '@/shared/api';
import { useTitleNotification, type TitleNotification } from '@/shared/lib';

const titleToggleIntervalMs: number = 5_000;

type UnreadMessageCountSubscription = {
  off: () => void;
};

type UnreadMessageTitleDependencies = {
  loadUnreadMessageCount: () => Promise<UnreadMessageCount | null>;
  subscribe: (
    callback: (unreadMessageCount: UnreadMessageCount) => void,
  ) => UnreadMessageCountSubscription;
};

type UnreadMessageTitleController = {
  notification: ComputedRef<TitleNotification | null>;
  start: () => Promise<void>;
  stop: () => void;
};

const createUnreadMessageTitleController = (
  dependencies: UnreadMessageTitleDependencies,
): UnreadMessageTitleController => {
  const unreadMessageCount = ref<number>(0);
  const isNotificationTitleVisible = ref<boolean>(true);
  const notification = computed<TitleNotification | null>(() => {
    if (unreadMessageCount.value <= 0 || !isNotificationTitleVisible.value) {
      return null;
    }

    return {
      title: {
        key: 'seo.unreadMessagesTitle',
        params: { count: unreadMessageCount.value },
      },
    };
  });
  let titleToggleTimer: number | null = null;
  let nextCheckTimer: number | null = null;
  let subscription: UnreadMessageCountSubscription | null = null;
  let requestVersion: number = 0;

  const clearTitleToggleTimer = (): void => {
    if (titleToggleTimer !== null) {
      window.clearInterval(titleToggleTimer);
      titleToggleTimer = null;
    }
  };

  const clearNextCheckTimer = (): void => {
    if (nextCheckTimer !== null) {
      window.clearTimeout(nextCheckTimer);
      nextCheckTimer = null;
    }
  };

  const updateTitleToggleTimer = (): void => {
    clearTitleToggleTimer();
    isNotificationTitleVisible.value = true;

    if (unreadMessageCount.value <= 0) {
      return;
    }

    titleToggleTimer = window.setInterval((): void => {
      isNotificationTitleVisible.value = !isNotificationTitleVisible.value;
    }, titleToggleIntervalMs);
  };

  const refresh = async (): Promise<void> => {
    const version: number = ++requestVersion;
    const nextUnreadMessageCount: UnreadMessageCount | null =
      await dependencies.loadUnreadMessageCount();

    if (nextUnreadMessageCount === null || version !== requestVersion) {
      return;
    }

    applyUnreadMessageCount(nextUnreadMessageCount);
  };

  const scheduleNextCheck = (nextCheckAt: string | undefined): void => {
    clearNextCheckTimer();

    if (!nextCheckAt) {
      return;
    }

    const timestamp: number = new Date(nextCheckAt).getTime();
    if (Number.isNaN(timestamp)) {
      return;
    }

    const delay: number = Math.max(0, timestamp - Date.now());
    nextCheckTimer = window.setTimeout((): void => {
      void refresh();
    }, delay);
  };

  const applyUnreadMessageCount = (value: UnreadMessageCount): void => {
    unreadMessageCount.value = value.count;
    updateTitleToggleTimer();
    scheduleNextCheck(value.nextCheckAt);
  };

  const start = async (): Promise<void> => {
    subscription = dependencies.subscribe((value: UnreadMessageCount): void => {
      requestVersion += 1;
      applyUnreadMessageCount(value);
    });
    await refresh();
  };

  const stop = (): void => {
    requestVersion += 1;
    subscription?.off();
    subscription = null;
    clearTitleToggleTimer();
    clearNextCheckTimer();
  };

  return { notification, start, stop };
};

const useUnreadMessageTitle = (): void => {
  const { getUnreadMessageCount } = useUnreadMessageCount();
  const controller = createUnreadMessageTitleController({
    loadUnreadMessageCount: async (): Promise<UnreadMessageCount | null> => {
      const result = await getUnreadMessageCount();
      return result.isSuccess ? (result.data ?? null) : null;
    },
    subscribe: MessageEvents.onUnreadMessageCountUpdated,
  });

  useTitleNotification(controller.notification);

  onMounted(() => {
    void controller.start();
  });
  onUnmounted(controller.stop);
};

export {
  createUnreadMessageTitleController,
  useUnreadMessageTitle,
  type UnreadMessageCountSubscription,
};
